import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import {
  toggleSideDrawer,
  closeSideDrawer
} from 'store/action/sideDrawerActions';
import DialerVisualizationForm from '../../components/Navbars/ServicePanel/DialerCallInformation/form';
import ThirdSystem from './ThirdSystem';

import {
  SideDrawerContainer,
  ButtonToggleContainer,
  ToggleButton,
  ToggleButtonText,
  ScrollWrapper
} from './styled';
import ProtocolCreationHandler from 'views/Protocol/form';
import LayoutSelector from 'components/Navbars/ServicePanel/ProtocolModal/components/LayoutSelector';
import { openSideDrawer } from 'store/action/sideDrawerActions';
import { changeProtocolCreationLayout } from 'store/action/sideDrawerActions';
import { PopoverBody, PopoverHeader, UncontrolledPopover } from 'reactstrap';
import { FaRegLightbulb } from 'react-icons/fa';
import { OutlineTooltip } from 'components/OutlineTooltip';
import ReactButton from 'components/Buttons/ReactButton';
import { IoMdClose } from 'react-icons/io';
import { loadObjectFromLocalStorage } from 'utils/fuctions/localStorage';
import { saveObjectToLocalStorage } from 'utils/fuctions/localStorage';
import { LOCAL_STORAGE_KEYS } from 'utils/fuctions/localStorage';
import { useSocketCallAnswered } from 'components/Navbars/ServicePanel/ProtocolModal/hooks/useSocketCallAnswered';

/**
 * Representa os dados da ligação que acabou de chegar
 * @typedef {Object} VisualizationFormData
 * @property {number} dialerId
 * @property {string} cdrDialerId
 * @property {string} extension
 * @property {Object} params
 * @property {string} params.nome_do_cliente
 * @property {string} params.identificacao
 * @property {string} params.whatsappNumber
 * @property {string} params.numero
 * @property {string} serviceChannel
 */

/**
 * Representa os dados do formulário de criação de protocolo
 * @typedef {Object} ProtocolCreationHandlerMemorization
 * @typedef {number} recordTypeId
 * @typedef {boolean} shouldGoDirectlyToTheForm - Se deve pular a seleção de tipo de registro ou não
 */

/**
 * @typedef {Object} SideDrawerTab
 * @property {number} id
 * @property {string} title
 * @property {VisualizationFormData | ProtocolCreationHandler | null} content
 * @property {TabState} state
 */

/** @enum {string} */
const TabState = {
  CLOSED: 'CLOSED',
  MINIMIZED: 'MINIMIZED',
  OPEN: 'OPEN'
};

/** @type {SideDrawerTab[]} */
const defaultTabs = [
  {
    id: 1,
    title: 'Dados do Atendimento',
    content: null,
    state: TabState.CLOSED
  },
  {
    id: 2,
    title: 'Tela Sistema',
    content: null,
    state: TabState.CLOSED
  },
  {
    id: 3,
    title: 'Criar protocolo',
    content: null,
    state: TabState.CLOSED
  }
];

const SideDrawer = () => {
  /**
   * Representa o estado da SideDrawer.
   *
   * @typedef {Object} SideDrawerState
   * @property {boolean} [isDrawerCollapsed]
   * @property {Object} [data]
   * @property {string|undefined} [data.tabId] - O id da tab selecionada, se existir
   */
  /** @type {SideDrawerState} */
  const sideDrawer = useSelector((state) => state.sideDrawer);
  /** @type {[SideDrawerTab[], React.Dispatch<React.SetStateAction<SideDrawerTab[]>>]} */
  const [tabs, setTabs] = useState(defaultTabs);
  const dispatch = useDispatch();

  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const sideDrawerRef = useRef(null);

  const memorizedProtocolCreationState = {
    ...loadObjectFromLocalStorage(
      LOCAL_STORAGE_KEYS.SIDEDRAWER_PROTOCOL_RECORD_TYPE_ID
    ),
    ...loadObjectFromLocalStorage(
      LOCAL_STORAGE_KEYS.SIDEDRAWER_PROTOCOL_FORM_VALUES
    )
  };

  const [previousTabs, setPreviousTabs] = useState(defaultTabs);

  const [createSubProtocol, setCreateSubProtocol] = useState(null);

  useEffect(() => {
    /** @type {Map<number, SideDrawerTab>} */
    const newTabs = new Map(
      tabs.map(
        /** @param {SideDrawerTab} tab */
        (tab) => [
          tab.id,
          {
            ...tab,
            state: tab.id === sideDrawer.data.tabId ? TabState.OPEN : tab.state
          }
        ]
      )
    );

    tabs
      .map((tab) => ({
        ...tab,
        state: tab.id === sideDrawer?.data?.tabId ? TabState.OPEN : tab.state
      }))
      .forEach((tab) => {
        if (tab.id === 1 && tab.state === TabState.OPEN) {
          newTabs.set(tab.id, {
            ...tab,
            // tenta ler os dados da ligação. caso não consiga, utiliza os dados do localStorage.
            content:
              sideDrawer?.data?.answeringDialerCallData ??
              (sideDrawer?.data?.tabId === 1
                ? loadObjectFromLocalStorage(
                    LOCAL_STORAGE_KEYS.SIDEDRAWER_ANSWERING_DIALER_CALL_DATA
                  )
                : null)
          });
        }

        if (tab.id === 3 && tab.state === TabState.OPEN) {
          newTabs.set(tab.id, {
            ...tab,
            content: memorizedProtocolCreationState
          });
        }
      });

    if (
      sideDrawer?.data?.tabId === 1 &&
      sideDrawer?.data?.answeringDialerCallData
    ) {
      // salva os dados da ligação que chegou no localStorage
      saveObjectToLocalStorage(
        LOCAL_STORAGE_KEYS.SIDEDRAWER_ANSWERING_DIALER_CALL_DATA,
        sideDrawer?.data?.answeringDialerCallData
      );
    }

    setTabs(Array.from(newTabs.values()));
  }, [sideDrawer, sideDrawer?.data?.answeringDialerCallData]);

  useEffect(() => {
    const handleScroll = () => {
      if (sideDrawerRef.current) {
        sideDrawerRef.current.style.top = `${window.scrollY}px`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const openTabs = tabs.filter((tab) => tab.state === TabState.OPEN);
    const previousOpenTabs = previousTabs.filter(
      (tab) => tab.state === TabState.OPEN
    );

    if (openTabs.length > 1) {
      const lastTab = openTabs.find(
        (tab) => !previousOpenTabs.some((prevTab) => prevTab.id === tab.id)
      );
      if (lastTab) {
        setTabs((prevTabs) =>
          prevTabs.map((tab) =>
            tab.id === lastTab.id ? { ...tab, state: TabState.MINIMIZED } : tab
          )
        );
      }
    }

    setPreviousTabs(tabs);
  }, [tabs, previousTabs]);

  useEffect(() => {
    const openTabs = tabs.filter((tab) => tab.state === TabState.OPEN);
    const previousOpenTabs = previousTabs.filter(
      (tab) => tab.state === TabState.OPEN
    );

    if (openTabs.length > previousOpenTabs.length) {
      const lastOpenedTab = openTabs.find(
        (tab) => !previousOpenTabs.some((prevTab) => prevTab.id === tab.id)
      );
      if (lastOpenedTab) {
        setTabs((prevTabs) =>
          prevTabs.map((tab) =>
            tab.id === lastOpenedTab.id
              ? { ...tab, state: TabState.OPEN }
              : tab.state === TabState.CLOSED
              ? tab
              : { ...tab, state: TabState.MINIMIZED }
          )
        );
      }
    }

    setPreviousTabs(tabs);
  }, [tabs, previousTabs]);

  const handleTabClick = (tabId) => {
    setTabs((prev) =>
      prev.map((tab) => {
        if (tab.id === tabId) {
          const newState =
            tab.state === TabState.OPEN ? TabState.MINIMIZED : TabState.OPEN;

          return {
            ...tab,
            state: newState
          };
        } else if (tab.state === TabState.OPEN) {
          return {
            ...tab,
            state: TabState.MINIMIZED
          };
        } else {
          return tab;
        }
      })
    );

    if (sideDrawer.data.tabId === tabId) {
      dispatch(toggleSideDrawer({ tabId }));
    } else {
      dispatch(openSideDrawer({ tabId }));
    }
  };

  const closeDrawer = useCallback(
    (tabId) => {
      const updatedTabs = tabs.map((tab) => {
        if (tab.id === tabId) {
          return {
            ...tab,
            state: TabState.CLOSED
          };
        }

        return tab;
      });

      setTabs(updatedTabs);
      dispatch(closeSideDrawer({ tabId }));
      setCreateSubProtocol(null);
    },
    [dispatch, tabs]
  );

  const handleLayoutChange = useCallback((layout) => {
    localStorage.setItem('selectedLayout', layout);
    if (layout === 'sidedrawer') {
      dispatch(openSideDrawer({ tabId: 3 }));
    } else {
      closeDrawer(3);
      dispatch(changeProtocolCreationLayout());
    }
  }, []);

  return (
    <div style={{ position: 'relative', height: '100vh' }}>
      <SideDrawerContainer
        ref={sideDrawerRef}
        style={{
          position:
            sideDrawer.isDrawerCollapsed &&
            !tabs.find((tab) => tab.state === TabState.OPEN)
              ? 'relative'
              : 'absolute',
          inset: 0,
          border: 0
        }}
      >
        <ButtonToggleContainer>
          {tabs.map(
            (tab) =>
              tab.state !== TabState.CLOSED && (
                <ToggleButton
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                >
                  <FiArrowLeft
                    className="mb-2"
                    size="12"
                    style={{
                      transform: `rotate(${
                        sideDrawer.isDrawerCollapsed ? '0deg' : '180deg'
                      })`,
                      transition: 'transform 0.6s'
                    }}
                  />
                  <ToggleButtonText>{tab.title}</ToggleButtonText>
                </ToggleButton>
              )
          )}
        </ButtonToggleContainer>
        <ScrollWrapper
          collapsed={sideDrawer.isDrawerCollapsed}
          style={{
            position: 'absolute',
            inset: 0,
            border: !sideDrawer.isDrawerCollapsed
              ? '1px solid #e1e1e1'
              : undefined
          }}
        >
          {tabs
            .filter((tab) => tab.state === TabState.OPEN)
            .map((tab) => {
              if (tab.id === 1) {
                return (
                  <DialerVisualizationForm
                    closeDrawer={() => closeDrawer(tab.id)}
                    data={tab.content}
                    key={tab.id}
                  />
                );
              } else if (tab.id === 2) {
                return (
                  <ThirdSystem
                    closeDrawer={() => closeDrawer(tab.id)}
                    key={tab.id}
                  />
                );
              } else if (tab.id === 3) {
                return (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'end'
                      }}
                    >
                      <h1 className="mb-0 h2" style={{ marginRight: 'auto' }}>
                        Criar protocolo
                      </h1>
                      <LayoutSelector
                        selectedLayout={sideDrawer}
                        onLayoutChange={handleLayoutChange}
                        systemConfiguration={systemConfiguration}
                      />
                      <IoMdClose
                        size={25}
                        cursor="pointer"
                        className="close"
                        id="PopoverClick"
                      />
                      <UncontrolledPopover
                        trigger="legacy"
                        placement="bottom"
                        target="PopoverClick"
                      >
                        <PopoverHeader>
                          <div className="d-flex justify-content-between align-items-center">
                            <span>Tem certeza?</span>
                            <div>
                              <FaRegLightbulb
                                cursor="help"
                                href="#"
                                id="UncontrolledTooltipExample"
                              />
                              <OutlineTooltip
                                placement="right"
                                target="UncontrolledTooltipExample"
                              >
                                <span className="small">
                                  Dica: se precisar de mais espaço na tela você
                                  pode minimizar esta janela clicando na aba
                                  lateral &quot;Criar protocolo&quot;.
                                </span>
                              </OutlineTooltip>
                            </div>
                          </div>
                        </PopoverHeader>
                        <PopoverBody>
                          Ao fechar a janela os dados atuais não poderão mais
                          ser visualizados.
                          <div className="mt-2 d-flex">
                            <ReactButton
                              type="button"
                              btnColor="confirmation"
                              onClick={() => {
                                localStorage.removeItem(
                                  LOCAL_STORAGE_KEYS.SIDEDRAWER_PROTOCOL_FORM_VALUES
                                );
                                localStorage.removeItem(
                                  LOCAL_STORAGE_KEYS.SIDEDRAWER_PROTOCOL_RECORD_TYPE_ID
                                );
                                closeDrawer(tab.id);
                              }}
                              size="sm"
                              block
                            >
                              Fechar mesmo assim
                            </ReactButton>
                          </div>
                        </PopoverBody>
                      </UncontrolledPopover>
                    </div>
                    <hr style={{ marginBlock: '1rem' }} />
                    {/*
                     * TODO:
                     * O componente `ProtocolCreationHandler` possui diversos hooks e lógicas que
                     * dependem da prop `modalActions`, especialmente do campo `defaultFormData`.
                     *
                     * Portanto, **`defaultFormData` não deveria ser `null`**.
                     *
                     * FIX:
                     * O componente `SideDrawer` deve receber como prop um objeto válido para `defaultFormData`.
                     *
                     * O valor adequado para essa prop pode ser obtido em:
                     * `flex-case-front-end/src/components/Navbars/ServicePanel/ProtocolModal/hooks/useSocketCallAnswered`
                     */}
                    <ProtocolCreationHandler
                      modalActions={{
                        closeModal: () => closeDrawer(3),
                        defaultFormData:
                          sideDrawer?.data?.answeringDialerCallData
                      }}
                      isFromSideDrawer={true}
                      selectedRecordTypeId={
                        loadObjectFromLocalStorage(
                          LOCAL_STORAGE_KEYS.SIDEDRAWER_PROTOCOL_RECORD_TYPE_ID
                        )?.memorizedRecordTypeId
                      }
                      createdFrom={sideDrawer?.data?.createdFrom ?? ''}
                      fatherProtocol={sideDrawer?.data?.fatherProtocol}
                      isFromUpdate={localStorage.getItem('isFromUpdate')}
                      createSubProtocol={createSubProtocol}
                      setCreateSubProtocol={setCreateSubProtocol}
                    />
                  </>
                );
              }
            })}
        </ScrollWrapper>
      </SideDrawerContainer>
    </div>
  );
};

export default SideDrawer;
